import { FormField, Pill, Select } from '@/shared/components';
import type { UserAccessResponse, UserPrivilegeOverrideEffect } from '@/shared/api';

const AREA_LABELS: Record<string, string> = {
  dashboard: 'Dashboard',
  properties: 'Objekte',
  cleaning: 'Reinigung',
  guests: 'Gastdaten',
  'guest-experience': 'Gästekommunikation',
};

export function UserAccessPrivilegeEditor({
  privileges,
  overrides,
  onChange,
}: {
  privileges: UserAccessResponse['privileges'];
  overrides: Record<string, UserPrivilegeOverrideEffect | undefined>;
  onChange: (privilegeId: string, effect: UserPrivilegeOverrideEffect | undefined) => void;
}) {
  const areas = privileges.reduce<Record<string, UserAccessResponse['privileges']>>(
    (grouped, privilege) => {
      (grouped[privilege.area] ??= []).push(privilege);
      return grouped;
    },
    {},
  );

  return (
    <section className="border-line/50 space-y-3 border-t pt-4">
      <div>
        <p className="text-ink-soft text-xs font-medium uppercase tracking-wider">
          Fachliche Rechte
        </p>
        <p className="text-ink-soft mt-1 text-sm">
          Rollenstandard bleibt sichtbar; einzelne Freigaben oder Entzüge werden serverseitig
          geprüft.
        </p>
      </div>
      {Object.entries(areas).map(([area, entries]) => (
        <fieldset key={area} className="border-line/50 space-y-2 rounded-md border p-3">
          <legend className="text-ink px-1 text-sm font-medium">{AREA_LABELS[area] ?? area}</legend>
          {entries.map((privilege) => {
            const value = overrides[privilege.id] ?? '';
            return (
              <div
                key={privilege.id}
                className="border-line/50 space-y-2 border-t pt-2 first:border-t-0 first:pt-0"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-ink text-sm font-medium">{privilege.id}</p>
                  <span className="flex flex-wrap gap-1">
                    <Pill variant="default" size="sm">
                      {privilege.level}
                    </Pill>
                    <Pill variant={privilege.isEffective ? 'live' : 'warning'} size="sm">
                      {privilege.isEffective ? 'wirksam' : 'derzeit nicht wirksam'}
                    </Pill>
                  </span>
                </div>
                <p className="text-ink-soft text-xs">
                  {privilege.fromPreset ? 'Rollenstandard aktiv' : 'Nicht im Rollenstandard'}
                  {privilege.overrideEffect ? ` · Gespeichert: ${privilege.overrideEffect}` : ''}
                </p>
                <FormField label="Abweichung">
                  <Select
                    value={value}
                    onChange={(event) =>
                      onChange(
                        privilege.id,
                        event.target.value === ''
                          ? undefined
                          : (event.target.value as UserPrivilegeOverrideEffect),
                      )
                    }
                  >
                    <option value="">Rollenstandard</option>
                    <option value="allow">Erlauben</option>
                    <option value="deny">Entziehen</option>
                  </Select>
                </FormField>
              </div>
            );
          })}
        </fieldset>
      ))}
    </section>
  );
}
